/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import {
  Box,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { CustomTextField } from '../../index';

const boxStyle = {
  mx: 0,
  my: 1,
  px: 3,
  py: 2,
};

const sectionHeaderStyle = {
  color: '#84d8c5',
  fontWeight: 700,
};

const boxSectionStyle = {
  bgcolor: 'rgb(26, 32, 44, 0.6)',
  display: 'flex',
  justifyContent: 'start',
  p: 2,
  mt: 2,
  borderRadius: '5px',
  position: 'relative',
  flexDirection: 'column',
};

export const EmployeeHistoryTab = (props) => {
  const handleChange = (field, value) => {
    props.setEmpHistory((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Box sx={{ ...boxStyle }}>
      <Box sx={boxSectionStyle}>
        <Typography variant="h5" sx={sectionHeaderStyle}>
          Criminal Background
        </Typography>
        <Grid container columnSpacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.empHistory.formallycharged}
                  onChange={
                    !props.viewOnly
                      ? (e) => {
                          const checked = e.target.checked;
                          handleChange('formallycharged', checked);
                          if (!checked) {
                            handleChange('chargedetails', '');
                          }
                        }
                      : undefined // read-only in viewOnly mode
                  }
                />
              }
              label="Have you ever been formally charged?"
            />
          </Grid>

          <Grid item xs={12}>
            {props.empHistory.formallycharged && (
              <CustomTextField
                errorMsg="Please enter an explanation"
                multiline
                label="Details on Charge"
                value={props.empHistory.chargedetails}
                onChange={(e) => {
                  handleChange('chargedetails', e.target.value);
                }}
                viewOnly={props.viewOnly}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.empHistory.guiltyoffense}
                  onChange={
                    !props.viewOnly
                      ? (e) => {
                          const checked = e.target.checked;
                          handleChange('guiltyoffense', checked);
                          if (!checked) {
                            handleChange('offensedetails', '');
                          }
                        }
                      : undefined // read-only in viewOnly mode
                  }
                />
              }
              label="Have you ever been guilty of any offense?"
            />
          </Grid>

          <Grid item xs={12}>
            {props.empHistory.guiltyoffense && (
              <CustomTextField
                errorMsg="Please enter an explanation"
                multiline
                label="Details on Offense"
                value={props.empHistory.offensedetails}
                onChange={(e) => {
                  handleChange('offensedetails', e.target.value);
                }}
                viewOnly={props.viewOnly}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.empHistory.convicted}
                  onChange={
                    !props.viewOnly
                      ? (e) => {
                          const checked = e.target.checked;
                          handleChange('convicted', checked);
                          if (!checked) {
                            handleChange('convictdetails', '');
                          }
                        }
                      : undefined // read-only in viewOnly mode
                  }
                />
              }
              label="Have you ever been convicted of any crime or violation of any law, decree, ordinance or regulation by any court or tribunal?"
            />
          </Grid>

          <Grid item xs={12}>
            {props.empHistory.convicted && (
              <CustomTextField
                errorMsg="Please enter an explanation"
                multiline
                label="Details on Conviction"
                value={props.empHistory.convictdetails}
                onChange={(e) => {
                  handleChange('convictdetails', e.target.value);
                }}
                viewOnly={props.viewOnly}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      <Box sx={boxSectionStyle}>
        <Typography variant="h5" sx={sectionHeaderStyle}>
          Previous employment
        </Typography>
        <Grid container columnSpacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.empHistory.terminatedemployment}
                  onChange={
                    !props.viewOnly
                      ? (e) => {
                          const checked = e.target.checked;
                          handleChange('terminatedemployment', checked);
                          if (!checked) {
                            handleChange('employmentdetails', '');
                          }
                        }
                      : undefined // read-only in viewOnly mode
                  }
                />
              }
              label="Have you ever been separated from the employment/service in any of the following modes: resignation, retirement, dropped from the rolls, dismissal, termination, end of term, finished contract, AWOL or phased out, in the public or private sector?"
            />
          </Grid>

          <Grid item xs={12}>
            {props.empHistory.terminatedemployment && (
              <CustomTextField
                errorMsg="Please enter an explanation"
                multiline
                label="Details on Previous Employment"
                value={props.empHistory.employmentdetails}
                onChange={(e) => {
                  handleChange('employmentdetails', e.target.value);
                }}
                viewOnly={props.viewOnly}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      <Box sx={boxSectionStyle}>
        <Typography variant="h5" sx={sectionHeaderStyle}>
          Other Information
        </Typography>
        <Grid container columnSpacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.empHistory.indigenousmember}
                  onChange={
                    !props.viewOnly
                      ? (e) => {
                          const checked = e.target.checked;
                          handleChange('indigenousmember', checked);
                          if (!checked) {
                            handleChange('indigenousdetails', '');
                          }
                        }
                      : undefined // read-only in viewOnly mode
                  }
                />
              }
              label="Are you a member of any indigenous group?"
            />
          </Grid>

          <Grid item xs={12}>
            {props.empHistory.indigenousmember && (
              <CustomTextField
                errorMsg="Please enter an explanation"
                label="Details on Membership"
                value={props.empHistory.indigenousdetails}
                onChange={(e) => {
                  handleChange('indigenousdetails', e.target.value);
                }}
                viewOnly={props.viewOnly}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.empHistory.diffabled}
                  onChange={
                    !props.viewOnly
                      ? (e) => {
                          const checked = e.target.checked;
                          handleChange('diffabled', checked);
                          if (!checked) {
                            handleChange('diffableddetails', '');
                          }
                        }
                      : undefined // read-only in viewOnly mode
                  }
                />
              }
              label="Are you differently abled?"
            />
          </Grid>

          <Grid item xs={12}>
            {props.empHistory.diffabled && (
              <CustomTextField
                errorMsg="Please enter an explanation"
                label="Details on Condition"
                value={props.empHistory.diffableddetails}
                onChange={(e) => {
                  handleChange('diffableddetails', e.target.value);
                }}
                viewOnly={props.viewOnly}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.empHistory.soloparent}
                  onChange={
                    !props.viewOnly
                      ? (e) => {
                          const checked = e.target.checked;
                          handleChange('soloparent', checked);
                          if (!checked) {
                            handleChange('soloparentdetails', '');
                          }
                        }
                      : undefined // read-only in viewOnly mode
                  }
                />
              }
              label="Are you a solo parent?"
            />
          </Grid>

          <Grid item xs={12}>
            {props.empHistory.soloparent && (
              <CustomTextField
                errorMsg="Please enter an explanation"
                label="Details on Being a Solo Parent"
                value={props.empHistory.soloparentdetails}
                onChange={(e) => {
                  handleChange('soloparentdetails', e.target.value);
                }}
                viewOnly={props.viewOnly}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
